import { PERMISSIONS, type Permission } from '@repo/shared-types/permissions';
import { ALL_ROLES, type Role } from '@repo/shared-types/roles';

/**
 * Role → permission mapping.
 *
 * This is the ONE place where the answer to "what can role X do?"
 * is defined. The FE reads this to decide which UI controls to
 * render; the BE reads this to gate middleware. If you add a new
 * role, update this map AND add the new role value to the db pgEnum
 * (see packages/db/src/schema/enums.ts).
 *
 * Design choices:
 *   - ADMIN gets every permission by definition. We compute it from
 *     ALL_PERMISSIONS rather than listing them, so adding a new
 *     permission to PERMISSIONS automatically grants it to ADMIN
 *     without forgetting to update this file.
 *   - SELLER is the daily sales user: customers + orders + read
 *     access to inventory and reports. No user or tenant admin.
 *     Slice 2 adds the full sales workflow (quotations, orders,
 *     commission rules) plus inventory VIEW (with COSTS, since
 *     margin reports depend on them).
 *   - WAREHOUSE is the inventory user: stock adjustments and read
 *     access to sales for picking. No customer data, no admin.
 *     Slice 1 adds catalog.view_products (read-only access to the
 *     product list) and CRM.view_clients (read-only customer data
 *     for fulfillment). The `defaultCost` field on `products` is
 *     stripped at the Resource layer — WAREHOUSE never sees it,
 *     even though they can call `GET /products/:id`.
 *     Slice 2 grants inventory.view_items + view_receipts +
 *     view_movements + manage_warehouses + create/approve/reject
 *     receipt (full receipt lifecycle so WAREHOUSE can run the
 *     inbound flow). NO inventory.view_costs: the avg_cost column
 *     is field-gated by `InventoryItemResource.toJson()` and the
 *     `unit_cost` column on warehouse_receipt_items is hidden by
 *     `WarehouseReceiptItemResource.toJson()`. WAREHOUSE also gets
 *     a READ-ONLY view of sales orders (for picking) but cannot
 *     create / update / cancel them.
 *
 *     Slice 5 (RF-13 International Purchases) grants:
 *       - SELLER: view + create + update + mark_in_transit + cancel
 *         + view_movements (the buyer-side workflow — write POs,
 *         mark them in-transit once the vendor ships, cancel DRAFTs).
 *         SELLER does NOT get approve (ADMIN-only, per CAP-2 R9) and
 *         does NOT get receive (WAREHOUSE-only, per CAP-3). SELLER
 *         CAN view the Movements tab on PO detail (the audit log
 *         the vendor can show back to the buyer in a dispute).
 *       - WAREHOUSE: view + receive + view_movements (the inbound
 *         side — receive a PO, see the movements on the PO detail).
 *         WAREHOUSE does NOT get create / update / approve /
 *         mark_in_transit / cancel (those belong to the SELLER /
 *         ADMIN workflow). The receive route is the only mutating
 *         surface the warehouse needs; field-level gating in
 *         `PurchaseOrderItemResource.toJson` (AD-7) hides the
 *         per-item prorrateo / finalUnitCost columns from WAREHOUSE.
 *       - ADMIN: every permission by definition (ALL_PERMISSION_VALUES).
 */
const ALL_PERMISSION_VALUES: readonly Permission[] = Object.values(PERMISSIONS).flatMap((group) =>
  Object.values(group),
) as unknown as readonly Permission[];

export const ROLE_PERMISSIONS: Readonly<Record<Role, readonly Permission[]>> = {
  ADMIN: ALL_PERMISSION_VALUES,
  SELLER: [
    // CRM — full client/provider access for the sales workflow.
    PERMISSIONS.CRM.VIEW_CLIENTS,
    PERMISSIONS.CRM.CREATE_CLIENT,
    PERMISSIONS.CRM.UPDATE_CLIENT,
    PERMISSIONS.CRM.DELETE_CLIENT,
    // Slice 2 — seller follow-up tasks (own task queue + per-client
    // create/complete/cancel from the CRM sheet). WAREHOUSE is
    // excluded (fulfillment never owns follow-ups); ADMIN gets it
    // implicitly via ALL_PERMISSION_VALUES.
    PERMISSIONS.CRM.MANAGE_TASKS,
    PERMISSIONS.CRM.VIEW_PROVIDERS,
    PERMISSIONS.CRM.CREATE_PROVIDER,
    PERMISSIONS.CRM.UPDATE_PROVIDER,
    PERMISSIONS.CRM.DELETE_PROVIDER,
    // Catalog — full product CRUD (cost visible); brand/model/type
    // management included so SELLERs can bootstrap their catalog
    // when working in small tenants.
    PERMISSIONS.CATALOG.VIEW_PRODUCTS,
    PERMISSIONS.CATALOG.CREATE_PRODUCT,
    PERMISSIONS.CATALOG.UPDATE_PRODUCT,
    PERMISSIONS.CATALOG.DELETE_PRODUCT,
    PERMISSIONS.CATALOG.MANAGE_BRANDS,
    PERMISSIONS.CATALOG.MANAGE_PRODUCT_MODELS,
    PERMISSIONS.CATALOG.MANAGE_PRODUCT_TYPES,
    PERMISSIONS.CATALOG.VIEW_PRICE_HISTORY,
    // Legacy CRM customers permissions (Slice 0) — keep until the
    // rename PR moves them. The new CRM.CLIENTS_* permissions are
    // the canonical Slice 1 set; VIEW/CREATE/UPDATE/DELETE_CUSTOMERS
    // remain so the existing Slice 0 customers screen keeps working
    // for SELLER until the FE migrates.
    PERMISSIONS.CRM.VIEW_CUSTOMERS,
    PERMISSIONS.CRM.CREATE_CUSTOMERS,
    PERMISSIONS.CRM.UPDATE_CUSTOMERS,
    // Inventory + sales — Slice 0 baseline.
    PERMISSIONS.INVENTORY.VIEW,
    PERMISSIONS.INVENTORY.VIEW_ITEMS,
    PERMISSIONS.INVENTORY.VIEW_COSTS,
    PERMISSIONS.INVENTORY.VIEW_RECEIPTS,
    PERMISSIONS.INVENTORY.VIEW_MOVEMENTS,
    PERMISSIONS.SALES.VIEW_ORDERS,
    PERMISSIONS.SALES.CREATE_ORDERS,
    PERMISSIONS.SALES.UPDATE_ORDERS,
    PERMISSIONS.SALES.CANCEL_ORDERS,
    // Slice 2 — full sales workflow for SELLER.
    PERMISSIONS.SALES.VIEW_ORDERS_DETAIL,
    PERMISSIONS.SALES.CREATE_ORDER,
    PERMISSIONS.SALES.UPDATE_ORDER,
    PERMISSIONS.SALES.APPROVE_ORDER,
    PERMISSIONS.SALES.CLOSE_ORDER,
    PERMISSIONS.SALES.CANCEL_ORDER,
    PERMISSIONS.SALES.VIEW_QUOTATIONS,
    PERMISSIONS.SALES.CREATE_QUOTATION,
    PERMISSIONS.SALES.UPDATE_QUOTATION,
    PERMISSIONS.SALES.SEND_QUOTATION,
    PERMISSIONS.SALES.CONVERT_QUOTATION,
    PERMISSIONS.SALES.VIEW_COMMISSION_RULES,
    PERMISSIONS.REPORTS.VIEW,
    // Slice 3 — Finance read-only views for the SELLER role.
    // SELLER can see receivables + payables + reports (read-only),
    // but cannot create / close / allocate / exclude. The CREATEs and
    // CLOSEs belong to a cashier / admin workflow — wiring those into
    // the SELLER role would give every rep the ability to mark a
    // client's invoice as paid without depositing the cash. The CLOSE
    // operations are split out so a future CASHIER role can get them
    // without inheriting the full SELLER set.
    //
    // Slice 6 (CAP-1, Movement-Gated Updates) adds UPDATE_PAYABLE to
    // the same restriction: SELLER does NOT get
    // finance.update_receivable nor finance.update_payable. Edits to
    // sensitive fields (totalAmount, currencyId) are admin-gated
    // because once a row has movement, only an admin should be
    // editing the financial shape. ADMIN gets UPDATE_PAYABLE
    // implicitly via ALL_PERMISSION_VALUES.
    PERMISSIONS.FINANCE.VIEW_RECEIVABLES,
    PERMISSIONS.FINANCE.VIEW_PAYABLES,
    PERMISSIONS.FINANCE.VIEW_COLLECTIONS,
    PERMISSIONS.FINANCE.VIEW_REPORTS,
    // Slice 4 — Dispatch + notifications (SELLER creates from APPROVED
    // sales order, then reads + gets notifications).
    // SELLER creates the dispatch (from an APPROVED sales order —
    // the warehouse then picks + marks ready). SELLER can also
    // cancel their own dispatch while it's still PENDING (the
    // cancel permission covers any non-DELIVERED/CANCELLED status,
    // so the route layer + the state machine are the actual
    // gatekeepers). SELLER does NOT approve (ADMIN only) and does
    // NOT mark-ready (WAREHOUSE only). SELLER gets notifications.view
    // so the bell badge in the FE works without extra wiring.
    PERMISSIONS.DISPATCH.VIEW,
    PERMISSIONS.DISPATCH.CREATE,
    PERMISSIONS.DISPATCH.CANCEL,
    PERMISSIONS.NOTIFICATIONS.VIEW,
    // Slice 5 — RF-13 International Purchases (SELLER: buyer-side).
    // SELLER authors DRAFT POs, edits them (DRAFT only — APPROVED
    // reverts to DRAFT on edit per AD-9), marks them in-transit
    // once the vendor ships, cancels DRAFTs, and reads the
    // Movements tab. SELLER does NOT approve (CAP-2 R9: ADMIN-only
    // because approval auto-generates a payable) and does NOT
    // receive (CAP-3: WAREHOUSE-only). The view permission covers
    // detail + list; view_movements is granted so the FE's Movements
    // tab on the PO detail page renders for SELLER.
    PERMISSIONS.PURCHASES.VIEW,
    PERMISSIONS.PURCHASES.CREATE,
    PERMISSIONS.PURCHASES.UPDATE,
    PERMISSIONS.PURCHASES.MARK_IN_TRANSIT,
    PERMISSIONS.PURCHASES.CANCEL,
    PERMISSIONS.PURCHASES.VIEW_MOVEMENTS,
    // Anexos — Transit / Consolidated Shipments (Feature A).
    // SELLER creates and manages shipments, adds expenses, and
    // configures distributions. WAREHOUSE gets read-only access
    // via purchases.view (already granted).
    PERMISSIONS.PURCHASES.MANAGE_SHIPMENTS,
    // Slice 8 — Authorizations (SELLER is VIEW-only in v1; cannot
    // create or respond. The visibility filter at the
    // AuthorizationService layer ensures SELLERs only see their own
    // requests in the inbox).
    PERMISSIONS.AUTHORIZATIONS.VIEW,
  ],
  WAREHOUSE: [
    // Catalog — read-only product list. The `defaultCost` field is
    // stripped by ProductResource.toJson() before serialization.
    PERMISSIONS.CATALOG.VIEW_PRODUCTS,
    // CRM — read-only clients (need names/addresses for fulfillment).
    PERMISSIONS.CRM.VIEW_CLIENTS,
    PERMISSIONS.CRM.VIEW_PROVIDERS,
    // Inventory + sales — Slice 0 baseline.
    PERMISSIONS.INVENTORY.VIEW,
    PERMISSIONS.INVENTORY.UPDATE,
    PERMISSIONS.INVENTORY.ADJUST_STOCK,
    PERMISSIONS.SALES.VIEW_ORDERS,
    // Slice 2 — full inventory operations EXCEPT cost visibility.
    // WAREHOUSE never sees avg_cost or unit_cost; the Resource layer
    // strips those fields during serialization. WAREHOUSE CAN manage
    // warehouses and run the receipt lifecycle end-to-end.
    PERMISSIONS.INVENTORY.VIEW_ITEMS,
    PERMISSIONS.INVENTORY.MANAGE_WAREHOUSES,
    PERMISSIONS.INVENTORY.CREATE_RECEIPT,
    PERMISSIONS.INVENTORY.VIEW_RECEIPTS,
    PERMISSIONS.INVENTORY.APPROVE_RECEIPT,
    PERMISSIONS.INVENTORY.REJECT_RECEIPT,
    PERMISSIONS.INVENTORY.VIEW_MOVEMENTS,
    // Slice 2 — READ-ONLY sales orders for picking. WAREHOUSE cannot
    // create / update / cancel sales orders.
    PERMISSIONS.SALES.VIEW_ORDERS_DETAIL,
    // Slice 4 — Dispatch + notifications (WAREHOUSE daily workflow).
    // WAREHOUSE sees the dispatch list (view), can mark a dispatch
    // ready (mark_ready — picks are done), and can resolve per-line
    // items (resolve_item — accept or reject each picked line).
    // WAREHOUSE does NOT get dispatch.create / approve / cancel —
    // those belong to the SELLER (create from APPROVED order) and
    // ADMIN (approve / cancel). WAREHOUSE gets notifications.view so
    // the bell badge shows "new dispatch assigned" prompts.
    PERMISSIONS.DISPATCH.VIEW,
    PERMISSIONS.DISPATCH.MARK_READY,
    PERMISSIONS.DISPATCH.RESOLVE_ITEM,
    PERMISSIONS.NOTIFICATIONS.VIEW,
    // Slice 5 — RF-13 International Purchases (WAREHOUSE: inbound).
    // WAREHOUSE receives IN_TRANSIT POs (the heart of the inbound
    // flow) and reads the Movements tab. NO create / update /
    // approve / mark_in_transit / cancel — those belong to the
    // SELLER / ADMIN. Field-level gating (AD-7) hides
    // prorrateoFreight / prorrateoCustoms / finalUnitCost from the
    // WAREHOUSE JSON responses; the route's field-gating does NOT
    // need a separate permission — the receive action itself is
    // gated by purchases.receive and the field omission is
    // enforced at the Resource layer.
    PERMISSIONS.PURCHASES.VIEW,
    PERMISSIONS.PURCHASES.RECEIVE,
    PERMISSIONS.PURCHASES.VIEW_MOVEMENTS,
    // Slice 8 — Authorizations (WAREHOUSE is the requester-side role
    // for the module in v1: files a request, sees only their own in
    // the inbox, can cancel a PENDING request, can attach files).
    // WAREHOUSE does NOT respond (the fan-out hits ADMINs only).
    PERMISSIONS.AUTHORIZATIONS.VIEW,
    PERMISSIONS.AUTHORIZATIONS.CREATE,
  ],
};

/**
 * Pure check — does the given role have the given permission?
 *
 * Used by FE render guards and BE middleware alike. This function
 * has no I/O and must stay allocation-free; the FE will call it on
 * every render of every gated control.
 */
export function roleHasPermission(role: Role, permission: Permission): boolean {
  return ROLE_PERMISSIONS[role]?.includes(permission) ?? false;
}

/**
 * Return the full permission set for a role — useful for hydrating
 * an AuthUser after login so the FE can do local checks without
 * round-tripping the API.
 */
export function permissionsForRole(role: Role): readonly Permission[] {
  return ROLE_PERMISSIONS[role] ?? [];
}

/**
 * Re-export for convenience — consumers that import Role from
 * `@repo/domain` should not need to know that the literal lives in
 * `@repo/shared-types`.
 */
export type { Role };

// Surface the role list for the few places that need to enumerate
// (admin forms, seed helpers, tests).
export { ALL_ROLES };
